import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// [F-WILLAPPLY] ApplyInterview — a William-es jelentkezési módok vezérlője a
// hirdetés-oldalon (mód-mátrix v2).
//
// A getJob 'interview' leírója (contract=2, szerver-oldali resolver) dönt:
//  - apply_mode_v2 (CV/űrlap-tengely): legacy | cv_form | cv_only_instant |
//    cv_silent_form
//  - william (effektív kapcsoló) + fill_mode (chat|manual) + clarifying
//
// Flow-k (post-apply tisztázó KIZÁRÓLAG a manual-űrlapos ágon):
//  - legacy + william+chat: nincs CV — William MINDEN űrlapmezőt chatben kérdez
//  - legacy + william+manual: klasszikus űrlap → beküldés után tisztázó chat
//  - cv_form: CV előre → előtöltött űrlap megmutatva (a jelölt pótol);
//    william+chat-tel a hiányzó mezők + tisztázók chatben, auto-beküldéssel;
//    william+manual-lal űrlap → beküldés után tisztázó chat
//  - cv_only_instant: CV → azonnali beküldés; williammel előtte rövid chat
//    (hiányzó kötelezők + tisztázók)
//  - cv_silent_form: CV → William a SZERVEREN kitölti az űrlapot → (williammel
//    tisztázó chat) → azonnali beküldés
// BÁRMILYEN hiba/limit a beküldés ELŐTT → klasszikus űrlap (a jelentkezés nem
// veszhet el); a beküldés UTÁN nincs űrlap-fallback (a tisztázó kör kimarad).
import React, { useState, useRef, useEffect } from 'react';
import { api } from '../../api';
import { ApplyForm } from '../ApplyForm/ApplyForm';
import { BookingSlotPicker } from '../ApplyForm/BookingSlotPicker';
// [F-LIAPPLY] a LinkedIn-profilos beküldés az ApplyForm-mal azonos követési/robot-ellenőrzési forrásokat használja
import { useTrackingParams } from '../../contexts/TrackingContext';
import { useSiteConfigOptional } from '../../hooks/useSiteConfig';
import { getRecaptchaToken, isRecaptchaEnabled, loadRecaptcha, RECAPTCHA_TOKEN_FIELD } from '../../utils/recaptcha';
import { adClickIdsAllowed } from '../../utils/consent';
let msgSeq = 0;
export function ApplyInterview({ interview, jobId, applyFormProps, t }) {
    // [F-WILLAPPLY] contract=2 mezők — hiányzó v2 kulcsnál a BC apply_mode-ból
    // származtatunk (régi szerver: 'chat' = william+chat, egyébként azonos mód)
    const bcMode = interview.apply_mode;
    const modeV2 = interview.apply_mode_v2
        || (bcMode === 'chat' ? 'cv_form' : bcMode === 'cv_only_review' ? 'cv_form' : bcMode);
    const william = interview.william !== undefined ? !!interview.william : bcMode === 'chat';
    const fillMode = interview.fill_mode === 'manual' ? 'manual' : 'chat';
    const clarifying = !!interview.clarifying;
    // [F-QUINN3 C.0] az ügynök neve a szerverről (a fallback-szövegekhez; a szerver-oldali
    // fordítás-feloldás ugyanígy cseréli a gyári szövegekben)
    const agentName = interview.agent_name || 'William';
    const hasCvStep = modeV2 === 'cv_form' || modeV2 === 'cv_only_instant' || modeV2 === 'cv_silent_form';
    // [F-LIAPPLY] LinkedIn-profilos mód: csak profil-link + e-mail + AV pipák — nincs CV, nincs űrlap, nincs chat
    const linkedinMode = modeV2 === 'linkedin';
    const chatFill = william && fillMode === 'chat' && !linkedinMode;
    // beküldés utáni tisztázó chat — kizárólag a kézi-űrlapos ágon
    const clarifyAfterApply = william && clarifying && fillMode === 'manual'
        && (modeV2 === 'legacy' || modeV2 === 'cv_form');
    // kezdő lépés: CV-s módok a feltöltéssel indulnak; legacy+chat a privacy-s
    // indítóképernyővel (CV nélkül); a LinkedIn-profilos mód a saját kétmezős kártyájával
    // (szintén 'upload' lépés); minden más a klasszikus űrlappal
    const initialStep = (hasCvStep || linkedinMode) ? 'upload' : (chatFill ? 'upload' : 'form');
    const [step, setStep] = useState(initialStep);
    const [privacyAccepted, setPrivacyAccepted] = useState(false);
    const [readAckAccepted, setReadAckAccepted] = useState(false);
    // [F-QUINN2] a teljes adatkezelési tájékoztató lenyitása (ApplyForm-paritás)
    const [showPrivacyContent, setShowPrivacyContent] = useState(false);
    const [cvFile, setCvFile] = useState(null);
    const [linkedinUrl, setLinkedinUrl] = useState('');
    // [F-LIAPPLY] a LinkedIn-profilos beküldés mezői és állapota
    const [linkedinEmail, setLinkedinEmail] = useState('');
    const [linkedinFailed, setLinkedinFailed] = useState(false);
    const [isProfileAccepted, setIsProfileAccepted] = useState(false);
    const [isNewsletterAccepted, setIsNewsletterAccepted] = useState(false);
    const tracking = useTrackingParams();
    const siteConfigOptional = useSiteConfigOptional();
    const recaptchaConfig = applyFormProps?.recaptcha !== undefined
        ? applyFormProps.recaptcha
        : ((siteConfigOptional && siteConfigOptional.recaptcha) || null);
    const recaptchaActive = isRecaptchaEnabled(recaptchaConfig);
    useEffect(() => {
        if (linkedinMode && recaptchaActive && recaptchaConfig) {
            loadRecaptcha(recaptchaConfig.site_key);
        }
    }, [linkedinMode, recaptchaActive, recaptchaConfig ? recaptchaConfig.site_key : '']);
    const [sessionUid, setSessionUid] = useState('');
    const [sessionSecret, setSessionSecret] = useState('');
    const [prefill, setPrefill] = useState({});
    const [storedCvName, setStoredCvName] = useState('');
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const [showReviewForm, setShowReviewForm] = useState(false);
    const [showPrefillPanel, setShowPrefillPanel] = useState(false);
    const fileInputRef = useRef(null);
    // chat állapot
    const [messages, setMessages] = useState([]);
    const [question, setQuestion] = useState(null);
    const [chatInput, setChatInput] = useState('');
    const [thinking, setThinking] = useState(false);
    const [pendingConfirm, setPendingConfirm] = useState('');
    // [F-QUINNTALK] a félretett következő kérdéshez tartozó reakció / a félretett lezáró válasz
    const pendingReactionRef = useRef('');
    const pendingDoneRef = useRef(null);
    // [F-CHATOPT] többválasztós gyorsválasz-gombok kijelölése (kérdés-váltáskor ürül)
    const [multiSelect, setMultiSelect] = useState([]);
    // [B-QORDER] a megerősítésre váró válasz MÖGÖTT álló következő kérdés — csak
    // a „Jó így” után kerül a chatbe (előbb rögzítés, aztán új kérdés)
    const pendingNextRef = useRef(null);
    // [F-CHATOPT] szinkron in-flight kapu: dupla kattintásból a második kérés el
    // sem indulhat (a thinking state csak a következő renderre frissül)
    const sendingRef = useRef(false);
    // [F-WILLAPPLY] beküldés utáni tisztázó-kör: a kör végén nincs apply — a
    // félretett apply-eredménnyel navigálunk a köszönő-oldalra
    const [postApplyChat, setPostApplyChat] = useState(false);
    const pendingApplyResultRef = useRef(null);
    const chatScrollRef = useRef(null);
    const pushMsg = (role, text) => {
        msgSeq += 1;
        const id = msgSeq;
        setMessages(prev => [...prev, { id, role, text }]);
        return id;
    };
    // [F-CHATOPT] stale kérésnél az optimista user-buborék visszavonása
    const removeMsg = (id) => {
        setMessages(prev => prev.filter(m => m.id !== id));
    };
    useEffect(() => {
        if (chatScrollRef.current) {
            chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
        }
    }, [messages, thinking, pendingConfirm, question]);
    // [F-CHATOPT] kérdés-váltáskor a többválasztós kijelölés ürül
    useEffect(() => {
        setMultiSelect([]);
    }, [question?.key]);
    /** [F-CHATOPT] azonos kulcsú, frissült kérdés-leírónál a kijelölés metszése
     * a friss opció-készletre — időközben törölt opció-id nem ragadhat be
     * (különben minden „Kész” újra fail-closed hibát kapna) */
    /** [F-QUINNTALK] a kérdés-buborék szövege: [reakció] [előtag] kérdés (a kérdés-szöveg tiszta marad) */
    const bubbleText = (q, reaction) => [reaction || '', q?.intro || '', q?.text || ''].filter((part) => part.trim() !== '').join(' ');
    const adoptQuestion = (q) => {
        setQuestion(q);
        const valid = new Set((q.options || []).map(o => o.id));
        setMultiSelect(prev => prev.filter(id => valid.has(id)));
    };
    /** legacy/fallback: sima ApplyForm (a session-előzményekkel, ha vannak) */
    const fallbackToForm = () => setStep('form');
    /** a tenant privacy-beállításai (a getJob apply_form.params-ból) */
    const privacyParams = applyFormProps?.params?.privacy || {};
    // [CU-869f3h1nd] Időpontfoglalás: a szerver a hirdetésen bekapcsolt foglalást
    // az űrlap VÉGÉRE illesztett 'booking' típusú (kötelező) itemként adja. A
    // William-es utakon (chat / azonnali beküldés / review) az űrlap nem jelenik
    // meg, ezért a beküldés ELŐTT itt kell időpontot választatni: a directSubmit a
    // hívás paramétereit félreteszi (pendingSubmitRef), és a jelölt a chat végén
    // (az utolsó William-kérdés UTÁN) vagy egy külön kártyán választ, majd a
    // választott slottal megy a beküldés. Nincs ilyen item → változatlan a flow.
    const bookingField = React.useMemo(() => {
        const sections = applyFormProps?.sections || [];
        for (const section of sections) {
            for (const item of (section.items || [])) {
                if (item && item.type === 'booking' && item.booking) {
                    return { param_name: item.param_name, booking: item.booking };
                }
            }
        }
        return null;
    }, [applyFormProps?.sections]);
    const [bookingValue, setBookingValue] = useState('');
    const [bookingPending, setBookingPending] = useState(false);
    const [bookingError, setBookingError] = useState('');
    const pendingSubmitRef = useRef(null);
    const bookingTheme = applyFormProps?.themeColors || {};
    const bookingColors = {
        primary: bookingTheme.primary || '#44bf00',
        inputBorder: bookingTheme.inputBorder || '#e3e6e6',
        inputBorderError: bookingTheme.inputBorderError || '#fb3b2f',
        text: bookingTheme.text || '#303030',
        textMuted: bookingTheme.textMuted || '#86888d',
        inputRadius: bookingTheme.inputRadius || '8px',
    };
    const bookingTranslations = applyFormProps?.formTranslations?.booking || {
        selectDay: 'Válassz napot',
        selectTime: 'Válassz időpontot',
        selected: 'Választott időpont',
        duration: 'Időtartam',
        location: 'Helyszín',
        online: 'Online',
        noSlots: 'Jelenleg nincs szabad időpont',
        minutes: 'perc',
    };
    /** az ApplyForm-oknak átadott kezdőértékek: a chatben/kártyán már választott időpont sem vész el */
    const formInitialValues = bookingField && bookingValue
        ? { ...prefill, [bookingField.param_name]: bookingValue }
        : prefill;
    /** navigáció a köszönő-oldalra (az eredeti onApplySuccess) */
    const finishApply = (result) => {
        if (applyFormProps.onApplySuccess) {
            applyFormProps.onApplySuccess(result);
        }
    };
    /** [F-WILLAPPLY] beküldés utáni tisztázó chat indítása — bármilyen hiba
     * esetén azonnal a köszönő-oldalra megyünk (a jelentkezés már él) */
    const startPostApplyClarify = async (result, uid, secret) => {
        pendingApplyResultRef.current = result;
        try {
            const resp = await api.interviewStart(jobId, uid, secret);
            if (!resp || !resp.ok || !resp.post_apply || !resp.question) {
                finishApply(result);
                return;
            }
            setSessionUid(uid);
            setSessionSecret(secret);
            setPostApplyChat(true);
            setStep('chat');
            pushMsg('william', t('interview_clarify_intro', 'A jelentkezésed megérkezett, köszönjük! Még néhány rövid kérdésem lenne — ha válaszolsz, gyorsabban tudunk veled foglalkozni.'));
            setQuestion(resp.question);
            pushMsg('william', bubbleText(resp.question));
        }
        catch (e) {
            finishApply(result);
        }
    };
    /** [F-WILLAPPLY] apply-siker elfogó: kézi-űrlapos ágon williammel jöhet a
     * beküldés utáni tisztázó kör (a szerver clarify_pending jelzése alapján) */
    const handleApplySuccess = (result) => {
        const data = result && result.data ? result.data : {};
        if (!clarifyAfterApply || !data.clarify_pending) {
            finishApply(result);
            return;
        }
        const uid = data.clarify_session_uid || sessionUid;
        const secret = data.clarify_session_secret || sessionSecret;
        if (!uid || !secret) {
            finishApply(result);
            return;
        }
        startPostApplyClarify(result, uid, secret);
    };
    /** az ApplyForm-oknak átadott propok — az apply-siker a mi elfogónkon megy át.
     * [B-QUINNCV] A CHAT-es utakról (William chatben kérdez) az űrlapra váltó
     * jelölt MINDIG tudjon CV-t csatolni: a chat-flow CV-központú, a váltással
     * ez ne vesszen el. Ha a job amúgy rejtené a CV-mezőt (is_cv_show=N — a
     * legacy+chat jobok tipikusan ilyenek), itt opcionálisként jelenik meg;
     * a szerver a beküldött CV-t ilyenkor is feldolgozza (csak a kötelezőség-
     * ellenőrzés kötődik a flaghez). A William+KÉZI űrlapos elsődleges út
     * (fill_mode=manual) a job konfigurációját követi — ott nem nyúlunk bele. */
    const wrappedApplyFormProps = {
        ...applyFormProps,
        ...(chatFill
            ? {
                isCvShow: true,
                isCvMandatory: applyFormProps.isCvShow ? applyFormProps.isCvMandatory : false,
            }
            : {}),
        onApplySuccess: handleApplySuccess,
    };
    /** közvetlen beküldés a kinyert adatokkal (cv_only/silent + chat done).
     * [CodexCR2] a secret explicit paraméter: a setSessionSecret utáni azonnali
     * hívás a React state-ből még a RÉGI értéket látná (stale closure).
     * [F-MULTIAPPLY] confirmed=true: a jelölt a review-képernyőn LÁTTA az
     * adatait — a 4901-es kapu nem foghatja meg újra (végtelen kör lenne). */
    const directSubmit = async (values, sid, instant = false, secretOverride, confirmed = false) => {
        // [CU-869f3h1nd] kötelező időpont-választás a beküldés ELŐTT: a paraméterek
        // félretéve, a jelölt a picker-en választ, majd a submitWithBooking folytatja
        if (bookingField && !bookingValue) {
            pendingSubmitRef.current = { values, sid, instant, secretOverride, confirmed };
            setBookingError('');
            setBookingPending(true);
            return false;
        }
        setBusy(true);
        setError('');
        try {
            const effectiveSecret = secretOverride || sessionSecret;
            const fd = new FormData();
            const submitValues = bookingField
                ? { ...values, [bookingField.param_name]: bookingValue }
                : values;
            Object.entries(submitValues).forEach(([key, value]) => {
                fd.append(key, Array.isArray(value) ? value.join(',') : String(value));
            });
            fd.append('job_id', String(jobId));
            fd.append('current_lang', applyFormProps.currentLang || 'hu');
            fd.append('privacy_accept', '1');
            // [F-PRIVTEXTVER] a megmutatott adatvédelmi szöveg aláírt verzió-tokenje
            if (privacyParams.text_version_token) {
                fd.append('privacy_text_version_token', String(privacyParams.text_version_token));
            }
            // [CodexCR] a tenant privacy-konfigja szerinti kötelező mezők
            if (privacyParams.privacy_mode === 'project') {
                fd.append('privacy_type', 'project');
            }
            if (privacyParams.read_acknowledgement_checkbox) {
                fd.append('read_acknowledgement', readAckAccepted ? '1' : '0');
            }
            fd.append('interview_session_uid', sid);
            if (effectiveSecret)
                fd.append('interview_session_secret', effectiveSecret);
            if (instant)
                fd.append('interview_instant', '1');
            if (confirmed)
                fd.append('interview_confirmed', '1');
            const result = await api.apply(fd);
            if (result && result.success === true) {
                finishApply(result);
                return true;
            }
            // [CodexCR] 4901: meglévő person instant-módban → megerősítéses review
            // hiányzó kötelező adat / bármi hiba → review űrlap a kinyert értékekkel
            const isReviewRequired = result && Number(result.errorCode) === 4901;
            setError(!isReviewRequired && result && result.errorMessage ? result.errorMessage : '');
            setShowReviewForm(true);
            setStep('review');
            return false;
        }
        catch (e) {
            setShowReviewForm(true);
            setStep('review');
            return false;
        }
        finally {
            setBusy(false);
        }
    };
    /** [CU-869f3h1nd] a félretett beküldés folytatása a választott időponttal */
    const submitWithBooking = async () => {
        if (!bookingValue) {
            setBookingError(t('interview_booking_required', 'A jelentkezés beküldéséhez válassz egy időpontot.'));
            return;
        }
        const parked = pendingSubmitRef.current;
        if (!parked) {
            return;
        }
        setBookingError('');
        setBookingPending(false);
        await directSubmit(parked.values, parked.sid, parked.instant, parked.secretOverride, parked.confirmed);
    };
    /** CV feltöltés + szinkron kinyerés (cv_silent_form-nál a szerver a néma
     * kitöltést is elvégzi, az eredmény a session-ben marad) */
    const handleExtract = async (file) => {
        setBusy(true);
        setError('');
        setStep('extracting');
        try {
            const resp = await api.interviewExtract(jobId, file, sessionUid || undefined, sessionSecret || undefined);
            if (!resp || !resp.ok) {
                // kapu zárva / hiba → legacy űrlap (a kiválasztott fájl átmegy az űrlapba)
                if (resp && resp.session_uid)
                    setSessionUid(resp.session_uid);
                if (resp && resp.session_secret)
                    setSessionSecret(resp.session_secret);
                fallbackToForm();
                return;
            }
            const sid = resp.session_uid || '';
            // [CodexCR2] a friss uid/secret/prefill EXPLICIT megy tovább — a state
            // csak a következő renderre frissül
            const secret = resp.session_secret || sessionSecret;
            setSessionUid(sid);
            if (resp.session_secret)
                setSessionSecret(resp.session_secret);
            setPrefill(resp.prefill || {});
            setStoredCvName(file.name);
            await routeAfterExtract(sid, secret, resp.prefill || {});
        }
        catch (e) {
            fallbackToForm();
        }
        finally {
            setBusy(false);
        }
    };
    /** [F-WILLAPPLY] a kinyerés utáni útvonal a mód-mátrix szerint */
    const routeAfterExtract = async (sid, secret, extractedPrefill) => {
        if (william && (chatFill || modeV2 === 'cv_only_instant' || modeV2 === 'cv_silent_form')) {
            // williames ágak: chat (hiányzó mezők / kötelezők + tisztázók) → beküldés
            await startChat(sid, secret, extractedPrefill);
            return;
        }
        if (modeV2 === 'cv_only_instant') {
            const ok = await directSubmit(extractedPrefill, sid, true, secret);
            if (!ok)
                return; // review-ra esett
            return;
        }
        if (modeV2 === 'cv_silent_form') {
            // william nélkül ide nem jutunk (a szerver cv_form-ra fokozza le) —
            // defenzív: azonnali beküldés a szerver-oldali kitöltéssel
            await directSubmit(extractedPrefill, sid, true, secret);
            return;
        }
        // cv_form (william nélkül vagy manual): előtöltött űrlap megmutatva
        setStep('form');
    };
    /** [F5] LinkedIn profil-import — a kinyert adatokkal ugyanaz az út, mint a CV-nél */
    const handleLinkedin = async (url) => {
        setBusy(true);
        setError('');
        setStep('extracting');
        try {
            const resp = await api.interviewLinkedin(jobId, url, sessionUid || undefined, sessionSecret || undefined);
            if (!resp || !resp.ok) {
                fallbackToForm();
                return;
            }
            const sid = resp.session_uid || '';
            const secret = resp.session_secret || sessionSecret;
            setSessionUid(sid);
            if (resp.session_secret)
                setSessionSecret(resp.session_secret);
            setPrefill(resp.prefill || {});
            await routeAfterExtract(sid, secret, resp.prefill || {});
        }
        catch (e) {
            fallbackToForm();
        }
        finally {
            setBusy(false);
        }
    };
    /** [F-LIAPPLY] LinkedIn-profilos jelentkezés beküldése: profil-link + e-mail + AV pipák.
     * A szerver szinkron beolvassa a profilt (név), létrehozza a személyt és a jelöltséget,
     * a részletes kitöltés (munkahelyek, képességek, nyelvek, profil-dokumentum) a háttérben
     * fut. 4902 = a profil nem olvasható be → hiba + űrlapos tartalék link. */
    const handleLinkedinApply = async () => {
        const url = linkedinUrl.trim();
        const email = linkedinEmail.trim();
        if (url === '' || email === '') {
            return;
        }
        setBusy(true);
        setError('');
        setLinkedinFailed(false);
        setStep('extracting');
        try {
            const fd = new FormData();
            fd.append('job_id', String(jobId));
            fd.append('current_lang', applyFormProps.currentLang || 'hu');
            fd.append('linkedin_url', url);
            fd.append('email', email);
            fd.append('privacy_accept', '1');
            // [F-PRIVTEXTVER] a megmutatott adatvédelmi szöveg aláírt verzió-tokenje
            if (privacyParams.text_version_token) {
                fd.append('privacy_text_version_token', String(privacyParams.text_version_token));
            }
            if (privacyParams.privacy_mode === 'project') {
                fd.append('privacy_type', 'project');
            }
            if (privacyParams.read_acknowledgement_checkbox) {
                fd.append('read_acknowledgement', readAckAccepted ? '1' : '0');
            }
            if (privacyParams.privacy_checkboxes?.is_profile) {
                fd.append('is_profile', isProfileAccepted ? '1' : '0');
            }
            if (privacyParams.privacy_checkboxes?.is_newsletter) {
                fd.append('is_newsletter', isNewsletterAccepted ? '1' : '0');
            }
            // követés (ApplyForm-paritás): utm hash + ajánló + forrás + utm_*; a hirdetési
            // kattintás-azonosítók (gclid/fbclid) kizárólag targeting-hozzájárulással
            if (tracking.utm)
                fd.append('utm', tracking.utm);
            if (tracking.referral_id)
                fd.append('referral_id', tracking.referral_id);
            if (tracking.get_referralId)
                fd.append('get_referralId', tracking.get_referralId);
            if (tracking.source)
                fd.append('source', tracking.source);
            const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
            if (adClickIdsAllowed()) {
                utmKeys.push('gclid', 'fbclid');
            }
            utmKeys.forEach(k => {
                const v = tracking[k];
                if (v && typeof v === 'string')
                    fd.append(k, v);
            });
            if (typeof window !== 'undefined') {
                fd.append('landing_url', window.location.href);
                fd.append('document_referrer', document.referrer || '');
            }
            // [F-APPLYGUARD] friss reCAPTCHA token a beküldéshez (a szerver a fizetős profil-import ELŐTT ellenőrzi)
            if (recaptchaActive && recaptchaConfig) {
                const recaptchaToken = await getRecaptchaToken(recaptchaConfig.site_key, recaptchaConfig.action || 'apply');
                if (recaptchaToken) {
                    fd.append(RECAPTCHA_TOKEN_FIELD, recaptchaToken);
                }
            }
            const result = await api.apply(fd);
            if (result && result.success === true) {
                finishApply(result);
                return;
            }
            const errorCode = result ? Number(result.errorCode) : 0;
            setLinkedinFailed(errorCode === 4902);
            setError(result && result.errorMessage
                ? result.errorMessage
                : t('interview_linkedin_error', 'A jelentkezés beküldése nem sikerült, kérjük próbáld újra.'));
            setStep('upload');
        }
        catch (e) {
            setLinkedinFailed(true);
            setError(t('interview_linkedin_error', 'A jelentkezés beküldése nem sikerült, kérjük próbáld újra.'));
            setStep('upload');
        }
        finally {
            setBusy(false);
        }
    };
    /** chat indítása (CV-vel vagy anélkül).
     * [CodexCR2] a secret és a friss prefill explicit paraméter — az extract
     * utáni azonnali hívásnál a state még a régi értékeket tartalmazná */
    const startChat = async (sid, secretOverride, prefillOverride) => {
        setBusy(true);
        try {
            const resp = await api.interviewStart(jobId, sid || undefined, (secretOverride || sessionSecret) || undefined);
            if (!resp || !resp.ok || resp.fallback) {
                fallbackToForm();
                return;
            }
            const effUid = resp.session_uid || sid;
            const effSecret = resp.session_secret || secretOverride || sessionSecret;
            setSessionUid(effUid);
            if (effSecret)
                setSessionSecret(effSecret);
            setStep('chat');
            if (resp.opening_question) {
                pushMsg('william', resp.opening_question);
            }
            if (resp.auto_answered && resp.auto_answered > 0) {
                pushMsg('note', t('interview_auto_answered', 'Néhány kérdésre a CV-d alapján már megvan a válasz — ezeket nem kérdezem újra.'));
            }
            if (resp.question) {
                setQuestion(resp.question);
                pushMsg('william', bubbleText(resp.question));
            }
            else {
                // nincs kérdés: azonnali beküldés a kinyert adatokkal.
                // [CodexCR3] instant-jelölővel: a jelentkező NEM látta szerkeszthető
                // űrlapon az összes értéket → meglévő personnál a szerver 4901-gyel
                // review-ra küldi (a directSubmit ezt kezeli)
                // [CU-869f3h1nd] időpontfoglalásnál előbb a picker (a directSubmit parkol)
                pushMsg('william', bookingField && !bookingValue
                    ? t('interview_booking_pick', 'Köszönöm, minden megvan! Már csak egy időpontot válassz az interjúra, és küldöm a jelentkezésed.')
                    : (resp.closing || t('interview_chat_done', 'Minden megvan, köszönöm! Már küldöm is a jelentkezésed.')));
                await directSubmit(prefillOverride || prefill, effUid, true, effSecret);
            }
        }
        catch (e) {
            fallbackToForm();
        }
        finally {
            setBusy(false);
        }
    };
    /** chat-válasz beküldése (a beküldés utáni tisztázó-körben is ez fut).
     * [F-CHATOPT] optionIds = gyorsválasz-gomb(ok); optimisticLabel = a kattintott
     * címke user-buborékja (stale válasznál visszavonjuk); gombnál nincs külön
     * megerősítő kör. */
    /** [F-QUINNTALK] a kör lezárása (done): reakció + záró mondat, majd beküldés / köszönő-oldal */
    const finishChatRound = async (resp) => {
        setQuestion(null);
        setPendingConfirm('');
        // [CodexCR] a foglalási (időpont-választó) felszólítás ELSŐBBSÉGET élvez a szerver záró mondata felett
        const closingText = (fallback, preferFallback = false) => [resp.reaction || '', preferFallback ? fallback : (resp.closing || fallback)].filter((part) => part.trim() !== '').join(' ');
        if (postApplyChat || resp.post_apply) {
            // tisztázó-kör vége: nincs beküldés, a jelentkezés már megvan
            pushMsg('william', closingText(t('interview_clarify_done', 'Köszönöm a válaszokat, ennyi volt! Hamarosan jelentkezünk.')));
            window.setTimeout(() => finishApply(pendingApplyResultRef.current), 1200);
            return;
        }
        const merged = { ...prefill, ...(resp.prefill || {}) };
        setPrefill(merged);
        // [CU-869f3h1nd] az utolsó William-kérdés UTÁN jön az időpont-választó
        // (a directSubmit parkol, amíg a jelölt nem választ) - a booking-szöveg elsőbbséget élvez
        pushMsg('william', bookingField && !bookingValue
            ? closingText(t('interview_booking_pick', 'Köszönöm, minden megvan! Már csak egy időpontot válassz az interjúra, és küldöm a jelentkezésed.'), true)
            : closingText(t('interview_chat_done', 'Minden megvan, köszönöm! Már küldöm is a jelentkezésed.')));
        // [CodexCR3] a chat-lezárás is auto-beküldés (a CV-prefill értékeit a
        // jelentkező nem látta űrlapon) → instant-jelölő = 4901 guard él
        await directSubmit(merged, sessionUid, true);
    };
    const sendChat = async (payload) => {
        // [F-CHATOPT] szinkron in-flight kapu: a thinking state aszinkron frissül,
        // dupla kattintásnál a második kérés itt akad el
        if (thinking || sendingRef.current)
            return;
        sendingRef.current = true;
        let pushedId = null;
        if (payload.text !== undefined) {
            pushedId = pushMsg('user', payload.text);
            setChatInput('');
        }
        else if (payload.optimisticLabel !== undefined) {
            pushedId = pushMsg('user', payload.optimisticLabel);
        }
        setThinking(true);
        try {
            const { optimisticLabel, ...apiPayload } = payload;
            void optimisticLabel;
            const resp = await api.interviewMessage(jobId, sessionUid, sessionSecret, {
                ...apiPayload,
                // [F-CHATOPT] stale/dupla kérés őr: melyik kérdésre válaszolunk
                expectedKey: question?.key,
            });
            if (!resp || resp.fallback) {
                // [F-WILLAPPLY] a beküldés UTÁN nincs űrlap-fallback — a jelentkezés
                // már él, a tisztázó kör kimarad, jöhet a köszönő-oldal
                if (postApplyChat) {
                    finishApply(pendingApplyResultRef.current);
                    return;
                }
                fallbackToForm();
                return;
            }
            if (!resp.ok) {
                // [F-CHATOPT] stale kérés (dupla katt / másik tab): csendes szinkron a
                // friss kérdés-leíróval — az optimista user-buborék is visszavonva
                if (resp.stale) {
                    if (pushedId !== null)
                        removeMsg(pushedId);
                    if (resp.question)
                        adoptQuestion(resp.question);
                    return;
                }
                if (resp.retry_message) {
                    pushMsg('william', resp.retry_message);
                }
                // a friss kérdés-leíró átvétele (pl. opció-készlet frissülhetett)
                if (resp.question)
                    adoptQuestion(resp.question);
                return;
            }
            // [F-QUINNTALK] az utolsó válasz megerősítése: ha a rögzített érték eltér a beírttól, a lezárás
            // (záró mondat + beküldés / köszönő-oldal) CSAK a Jó így után fut; Javítom → redo, a félretett
            // lezárás eldobva
            if (resp.done && resp.display && payload.text !== undefined) {
                setPendingConfirm(resp.display);
                pendingNextRef.current = null;
                pendingDoneRef.current = resp;
                return;
            }
            if (resp.done) {
                await finishChatRound(resp);
                return;
            }
            // [B-QORDER] ELŐBB a rögzítés-megerősítés, UTÁNA a következő kérdés: a
            // következő kérdés-leírót félretesszük, és csak a Jó így után
            // buborékoljuk — így a chatben nem előzi meg a kérdés a megerősítést
            // [F-QUINNTALK] a reakció a félretett kérdéssel együtt utazik
            if (resp.display && payload.text !== undefined) {
                setPendingConfirm(resp.display);
                pendingNextRef.current = resp.question || null;
                pendingReactionRef.current = resp.reaction || '';
                return;
            }
            if (resp.question) {
                setQuestion(resp.question);
                pushMsg('william', bubbleText(resp.question, resp.reaction));
            }
        }
        catch (e) {
            if (postApplyChat) {
                finishApply(pendingApplyResultRef.current);
                return;
            }
            fallbackToForm();
        }
        finally {
            sendingRef.current = false;
            setThinking(false);
        }
    };
    /** [F-CHATOPT] egyválasztós gyorsválasz-gomb: katt = válasz (a katt maga a
     * megerősítés — nincs külön „Jó így” kör) */
    const sendOption = (id, label) => {
        if (thinking || sendingRef.current || pendingConfirm)
            return;
        sendChat({ optionIds: [id], optimisticLabel: label });
    };
    /** [F-CHATOPT] többválasztós „Kész”: a kijelölt opciók beküldése */
    const sendMultiOptions = () => {
        if (thinking || sendingRef.current || pendingConfirm || multiSelect.length === 0 || !question?.options)
            return;
        const labels = question.options
            .filter(o => multiSelect.includes(o.id))
            .map(o => o.label);
        sendChat({ optionIds: multiSelect, optimisticLabel: labels.join(', ') });
    };
    /** [F-CHATOPT] többválasztós chip ki-/bekapcsolása */
    const toggleMultiOption = (id) => {
        if (thinking || sendingRef.current || pendingConfirm)
            return;
        setMultiSelect(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
    };
    // ---- render ----
    /** [F-WILLAPPLY] lenyitható panel: amit William a CV-ből kitöltött */
    const renderPrefillPanel = () => {
        const entries = Object.entries(prefill).filter(([, v]) => v !== '' && v !== null);
        if (entries.length === 0) {
            return null;
        }
        return (_jsxs("div", { className: "career-interview-prefill-panel", children: [_jsx("button", { type: "button", className: "career-interview-link career-interview-prefill-toggle", onClick: () => setShowPrefillPanel(!showPrefillPanel), children: showPrefillPanel
                        ? t('interview_prefill_hide', 'Kitöltött adatok elrejtése')
                        : t('interview_prefill_show', `Mit töltött ki ${agentName} a CV-mből?`) }), showPrefillPanel && (_jsx("ul", { className: "career-interview-prefill", children: entries.map(([key, value]) => (_jsx("li", { children: Array.isArray(value) ? value.join(', ') : String(value) }, key))) }))] }));
    };
    /** [CU-869f3h1nd] Időpontfoglalás vezérlő + beküldés gomb (chat alatt / külön kártyán) */
    const renderBookingPicker = () => {
        if (!bookingField) {
            return null;
        }
        return (_jsxs("div", { className: "career-interview-booking", "data-testid": "interview-booking", children: [_jsx(BookingSlotPicker, { descriptor: bookingField.booking, value: bookingValue, onChange: (v) => { setBookingValue(v); setBookingError(''); }, paramName: bookingField.param_name, hasError: !!bookingError, lang: applyFormProps?.currentLang || 'hu', colors: bookingColors, translations: bookingTranslations }), bookingError && _jsx("p", { className: "career-interview-error", children: bookingError }), _jsx("div", { className: "career-interview-actions", children: _jsx("button", { type: "button", className: "career-interview-btn-primary", "data-testid": "interview-booking-submit", disabled: busy || !bookingValue, onClick: submitWithBooking, children: busy ? t('interview_submitting', 'Küldés...') : t('interview_booking_submit', 'Időpont foglalása és jelentkezés beküldése') }) })] }));
    };
    if (bookingPending && bookingField && step !== 'chat' && step !== 'form') {
        // [CU-869f3h1nd] nem-chat William-utak (azonnali beküldés / review): külön kártya
        return (_jsxs("div", { className: "career-interview", children: [_jsxs("div", { className: "career-interview-card", children: [_jsx("p", { className: "career-interview-lead", children: t('interview_booking_pick', 'Köszönöm, minden megvan! Már csak egy időpontot válassz az interjúra, és küldöm a jelentkezésed.') }), error && _jsx("p", { className: "career-interview-error", children: error }), renderBookingPicker()] }), _jsx("button", { type: "button", className: "career-interview-link", onClick: () => { setBookingPending(false); fallbackToForm(); }, children: t('interview_switch_form', 'Kitöltés űrlapon') })] }));
    }
    if (step === 'form') {
        // legacy / fallback / cv_form nézet — a kinyert adatokkal előtöltve.
        // fallback: a már kiválasztott fájl átkerül az űrlapba (nem vész el).
        return (_jsx(ApplyForm, { ...wrappedApplyFormProps, initialValues: formInitialValues, interviewSessionUid: sessionUid, interviewSessionSecret: sessionSecret, storedCvName: storedCvName, initialCvFile: cvFile || undefined }));
    }
    if (step === 'review') {
        const prefillEntries = Object.entries(prefill).filter(([, v]) => v !== '' && v !== null);
        return (_jsxs("div", { className: "career-interview", children: [_jsxs("div", { className: "career-interview-card", children: [_jsx("p", { className: "career-interview-lead", children: t('interview_review_lead', `${agentName} kiolvasta az adataidat az önéletrajzodból:`) }), prefillEntries.length > 0 ? (_jsx("ul", { className: "career-interview-prefill", children: prefillEntries.map(([key, value]) => (_jsx("li", { children: Array.isArray(value) ? value.join(', ') : String(value) }, key))) })) : (_jsx("p", { className: "career-interview-muted", children: t('interview_review_empty', 'Az önéletrajzból nem sikerült adatot kiolvasni — kérjük, töltsd ki az űrlapot.') })), error && _jsx("p", { className: "career-interview-error", children: error }), _jsxs("div", { className: "career-interview-actions", children: [!showReviewForm && prefillEntries.length > 0 && (_jsx("button", { type: "button", className: "career-interview-btn-primary", disabled: busy, onClick: () => directSubmit(prefill, sessionUid, false, undefined, true), children: busy ? t('interview_submitting', 'Küldés...') : t('interview_submit', 'Jelentkezés beküldése') })), _jsx("button", { type: "button", className: "career-interview-btn-secondary", onClick: () => setShowReviewForm(!showReviewForm), children: showReviewForm ? t('interview_hide_form', 'Űrlap elrejtése') : t('interview_review_form', 'Adataim ellenőrzése') })] })] }), (showReviewForm || prefillEntries.length === 0) && (_jsx(ApplyForm, { ...wrappedApplyFormProps, initialValues: formInitialValues, interviewSessionUid: sessionUid, interviewSessionSecret: sessionSecret, storedCvName: storedCvName }))] }));
    }
    if (step === 'chat') {
        return (_jsxs("div", { className: "career-interview career-interview-chat", children: [renderPrefillPanel(), _jsxs("div", { className: "career-interview-chat-messages", ref: chatScrollRef, children: [messages.map(m => (_jsx("div", { className: `career-interview-msg career-interview-msg-${m.role}`, children: _jsx("div", { className: "career-interview-bubble", children: m.text }) }, m.id))), thinking && (_jsx("div", { className: "career-interview-msg career-interview-msg-william", children: _jsx("div", { className: "career-interview-bubble career-interview-typing", children: t('interview_typing', `${agentName} gépel...`) }) })), pendingConfirm && (_jsxs("div", { className: "career-interview-confirm", children: [_jsxs("span", { children: [t('interview_recorded', 'Ezt rögzítem:'), " ", _jsx("strong", { children: pendingConfirm })] }), _jsx("button", { type: "button", className: "career-interview-btn-primary", onClick: () => {
                                        // [B-QORDER] megerősítés után jön a félretett következő kérdés
                                        setPendingConfirm('');
                                        // [F-QUINNTALK] a félretett lezárás (utolsó válasz megerősítése) vagy a következő kérdés reakcióval
                                        const doneResp = pendingDoneRef.current;
                                        pendingDoneRef.current = null;
                                        if (doneResp) {
                                            void finishChatRound(doneResp);
                                            return;
                                        }
                                        const next = pendingNextRef.current;
                                        const reaction = pendingReactionRef.current;
                                        pendingNextRef.current = null;
                                        pendingReactionRef.current = '';
                                        if (next) {
                                            setQuestion(next);
                                            pushMsg('william', bubbleText(next, reaction));
                                        }
                                    }, children: t('interview_confirm_ok', 'Jó így') }), _jsx("button", { type: "button", className: "career-interview-btn-secondary", onClick: () => {
                                        // [B-QORDER] javításnál a félretett kérdés eldobva — a redo
                                        // válasza adja vissza az újrakérdezett kérdést
                                        setPendingConfirm('');
                                        pendingNextRef.current = null;
                                        pendingReactionRef.current = '';
                                        pendingDoneRef.current = null;
                                        sendChat({ redo: true });
                                    }, children: t('interview_confirm_edit', 'Javítom') })] })), question && question.options && question.options.length > 0 && !pendingConfirm && !thinking && (_jsxs("div", { className: "career-interview-options", children: [question.options.map(opt => (_jsx("button", { type: "button", className: `career-interview-option-chip${question.multi && multiSelect.includes(opt.id) ? ' career-interview-option-chip-selected' : ''}`, "aria-pressed": question.multi ? multiSelect.includes(opt.id) : undefined, disabled: busy, onClick: () => (question.multi ? toggleMultiOption(opt.id) : sendOption(opt.id, opt.label)), children: opt.label }, opt.id))), question.multi && (_jsx("button", { type: "button", className: "career-interview-btn-primary career-interview-options-done", disabled: busy || multiSelect.length === 0, onClick: sendMultiOptions, children: t('interview_options_done', 'Kész') }))] })), bookingPending && !thinking && renderBookingPicker()] }), _jsxs("div", { className: "career-interview-inputbar", children: [_jsx("input", { type: "text", className: "career-interview-input", placeholder: t('interview_input_placeholder', 'Írd ide a válaszod...'), value: chatInput, disabled: thinking || !!pendingConfirm || !question || busy, onChange: e => setChatInput(e.target.value), onKeyDown: e => {
                                if (e.key === 'Enter' && chatInput.trim() !== '') {
                                    e.preventDefault();
                                    sendChat({ text: chatInput.trim() });
                                }
                            } }), _jsx("button", { type: "button", className: "career-interview-btn-primary", disabled: thinking || !!pendingConfirm || !question || busy || chatInput.trim() === '', onClick: () => sendChat({ text: chatInput.trim() }), children: t('interview_send', 'Küldés') })] }), _jsxs("div", { className: "career-interview-footer", children: [question && question.skippable && !pendingConfirm && (_jsx("button", { type: "button", className: "career-interview-link", onClick: () => sendChat({ skip: true }), children: t('interview_skip', 'Kihagyom') })), postApplyChat ? (_jsx("button", { type: "button", className: "career-interview-link", onClick: () => finishApply(pendingApplyResultRef.current), children: t('interview_clarify_skip_all', 'Befejezem — a jelentkezésem így is él') })) : (_jsx("button", { type: "button", className: "career-interview-link", onClick: () => { setBookingPending(false); fallbackToForm(); }, children: t('interview_switch_form', 'Kitöltés űrlapon') }))] })] }));
    }
    // [F-LIAPPLY] LinkedIn-profilos mód: profil-link + e-mail + AV pipák (nincs CV, nincs űrlap);
    // a beküldés gomb LinkedIn-színű; hibánál (a profil nem olvasható be) űrlapos tartalék
    if (linkedinMode) {
        const linkedinBusy = busy || step === 'extracting';
        const canSubmitLinkedin = linkedinUrl.trim() !== '' && linkedinEmail.trim() !== '' && privacyAccepted
            && (!privacyParams?.read_acknowledgement_checkbox || readAckAccepted) && !linkedinBusy;
        return (_jsx("div", { className: "career-interview", children: _jsxs("div", { className: "career-interview-card career-interview-linkedin-card", children: [_jsx("p", { className: "career-interview-lead", children: t('interview_linkedin_lead', 'Nem kell önéletrajz és űrlap: add meg a LinkedIn profilod linkjét és az e-mail címed, a többit kiolvassuk a profilodból.') }), _jsxs("label", { className: "career-interview-field", children: [_jsx("span", { className: "career-interview-field-label", children: t('interview_linkedin_url_label', 'LinkedIn profil linkje') }), _jsx("input", { type: "url", className: "career-interview-input career-interview-linkedin", placeholder: t('interview_linkedin_url_placeholder', 'https://www.linkedin.com/in/profilnev'), value: linkedinUrl, disabled: linkedinBusy, autoComplete: "url", inputMode: "url", "data-testid": "linkedin-apply-url", onChange: e => setLinkedinUrl(e.target.value) })] }), _jsxs("label", { className: "career-interview-field", children: [_jsx("span", { className: "career-interview-field-label", children: t('interview_linkedin_email_label', 'E-mail cím') }), _jsx("input", { type: "email", className: "career-interview-input career-interview-linkedin", placeholder: "nev@pelda.hu", value: linkedinEmail, disabled: linkedinBusy, autoComplete: "email", inputMode: "email", "data-testid": "linkedin-apply-email", onChange: e => setLinkedinEmail(e.target.value) })] }), _jsx("p", { className: "career-interview-privacy-intro", style: { fontWeight: 600, margin: '0 0 8px 0' }, dangerouslySetInnerHTML: { __html: applyFormProps?.formTranslations?.privacyIntro
                                || t('apply_privacy_intro', 'Kérjük az alábbi tájékoztatót figyelmesen olvassa el, majd a szöveg alatt található jelölőnégyzet segítségével nyilatkozzon adatainak kezeléséről.') } }), privacyParams?.privacy_content && (_jsxs("div", { className: "career-interview-privacy-info", children: [_jsx("button", { type: "button", className: "career-interview-link career-interview-privacy-info-link", "aria-expanded": showPrivacyContent, "aria-controls": "career-interview-privacy-info-content", onClick: () => setShowPrivacyContent(!showPrivacyContent), children: applyFormProps?.formTranslations?.readPrivacyInfo
                                    || t('apply_read_privacy_info', 'Olvastam az adatvédelmi tájékoztatót') }), showPrivacyContent && (_jsx("div", { id: "career-interview-privacy-info-content", className: "career-interview-privacy-info-content", dangerouslySetInnerHTML: { __html: privacyParams.privacy_content } }))] })), _jsxs("label", { className: "career-interview-privacy", children: [_jsx("input", { type: "checkbox", checked: privacyAccepted, "data-testid": "linkedin-apply-privacy", onChange: e => setPrivacyAccepted(e.target.checked) }), privacyParams?.texts?.newTxt1 ? (_jsx("span", { dangerouslySetInnerHTML: { __html: privacyParams.texts.newTxt1 } })) : (_jsx("span", { children: t('interview_linkedin_privacy', 'Elfogadom az adatkezelési tájékoztatót, és hozzájárulok, hogy a LinkedIn profilom adatait feldolgozzátok.') }))] }), privacyParams?.read_acknowledgement_checkbox && (_jsxs("label", { className: "career-interview-privacy", children: [_jsx("input", { type: "checkbox", checked: readAckAccepted, "data-testid": "linkedin-apply-readack", onChange: e => setReadAckAccepted(e.target.checked) }), privacyParams?.texts?.privacy_text && !privacyParams?.privacy_checkboxes?.is_profile ? (_jsx("span", { dangerouslySetInnerHTML: { __html: privacyParams.texts.privacy_text } })) : (_jsx("span", { children: t('interview_read_ack', 'Elolvastam és megértettem az adatkezelési tájékoztatót.') }))] })), privacyParams?.privacy_checkboxes?.is_profile && (_jsxs("label", { className: "career-interview-privacy", children: [_jsx("input", { type: "checkbox", checked: isProfileAccepted, "data-testid": "linkedin-apply-isprofile", onChange: e => setIsProfileAccepted(e.target.checked) }), _jsx("span", { dangerouslySetInnerHTML: { __html: privacyParams?.texts?.privacy_text
                                        || applyFormProps?.formTranslations?.privacy?.isProfile?.text
                                        || t('apply_profile_text', 'Profilom megjelenítése') } })] })), privacyParams?.privacy_checkboxes?.is_newsletter && (_jsxs("label", { className: "career-interview-privacy", children: [_jsx("input", { type: "checkbox", checked: isNewsletterAccepted, "data-testid": "linkedin-apply-isnewsletter", onChange: e => setIsNewsletterAccepted(e.target.checked) }), _jsx("span", { dangerouslySetInnerHTML: { __html: privacyParams?.texts?.newsletter_text
                                        || applyFormProps?.formTranslations?.privacy?.isNewsletter?.text
                                        || t('apply_newsletter_text', 'Hírlevélre feliratkozom') } })] })), error && _jsx("p", { className: "career-interview-error", "data-testid": "linkedin-apply-error", children: error }), _jsx("div", { className: "career-interview-actions", children: _jsxs("button", { type: "button", className: "career-interview-btn-primary career-interview-btn-linkedin", disabled: !canSubmitLinkedin, "data-testid": "linkedin-apply-submit", onClick: handleLinkedinApply, children: [_jsx("svg", { width: "18", height: "18", viewBox: "0 0 24 24", fill: "currentColor", "aria-hidden": "true", children: _jsx("path", { d: "M20.45 20.45h-3.56v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.13 1.45-2.13 2.94v5.67H9.35V9h3.41v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28zM5.34 7.43a2.06 2.06 0 1 1 0-4.13 2.06 2.06 0 0 1 0 4.13zM7.12 20.45H3.56V9h3.56v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.73v20.54C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.73V1.73C24 .77 23.2 0 22.22 0z" }) }), _jsx("span", { children: step === 'extracting'
                                        ? t('interview_linkedin_importing', `${agentName} beolvassa a LinkedIn profilod...`)
                                        : t('interview_linkedin_submit', 'Jelentkezés a LinkedIn profilommal') })] }) }), linkedinFailed && (_jsx("button", { type: "button", className: "career-interview-link", "data-testid": "linkedin-apply-fallback", onClick: fallbackToForm, children: t('interview_switch_form', 'Kitöltés űrlapon') }))] }) }));
    }
    // upload / extracting lépés: CV-s módok belépője; legacy+chat-nél CV nélküli
    // indítóképernyő (privacy + Kezdjük)
    const cvControlsVisible = hasCvStep;
    return (_jsx("div", { className: "career-interview", children: _jsxs("div", { className: "career-interview-card", children: [_jsx("p", { className: "career-interview-lead", children: !cvControlsVisible
                        ? t('interview_start_lead_nocv', `${agentName} chatben, pár kérdéssel végigvisz a jelentkezésen — űrlap kitöltése nélkül.`)
                        : modeV2 === 'cv_silent_form'
                            ? t('interview_upload_lead_silent', `Csak dobd be az önéletrajzod — ${agentName} kiolvassa az adataidat, kitölti helyetted a jelentkezést, és már küldi is.`)
                            : chatFill
                                ? t('interview_upload_lead_chat', `Dobd be az önéletrajzod, és ${agentName} chatben, pár kérdéssel végigvisz a jelentkezésen.`)
                                : t('interview_upload_lead', `Csak dobd be az önéletrajzod — ${agentName} kiolvassa belőle az adataidat, neked csak a hiányzókat kell pótolnod.`) }), _jsx("p", { className: "career-interview-privacy-intro", style: { fontWeight: 600, margin: '0 0 8px 0' }, dangerouslySetInnerHTML: { __html: applyFormProps?.formTranslations?.privacyIntro
                            || t('apply_privacy_intro', 'Kérjük az alábbi tájékoztatót figyelmesen olvassa el, majd a szöveg alatt található jelölőnégyzet segítségével nyilatkozzon adatainak kezeléséről.') } }), privacyParams?.privacy_content && (_jsxs("div", { className: "career-interview-privacy-info", children: [_jsx("button", { type: "button", className: "career-interview-link career-interview-privacy-info-link", "aria-expanded": showPrivacyContent, "aria-controls": "career-interview-privacy-info-content", onClick: () => setShowPrivacyContent(!showPrivacyContent), children: applyFormProps?.formTranslations?.readPrivacyInfo
                                || t('apply_read_privacy_info', 'Olvastam az adatvédelmi tájékoztatót') }), showPrivacyContent && (_jsx("div", { id: "career-interview-privacy-info-content", className: "career-interview-privacy-info-content", dangerouslySetInnerHTML: { __html: privacyParams.privacy_content } }))] })), _jsxs("label", { className: "career-interview-privacy", children: [_jsx("input", { type: "checkbox", checked: privacyAccepted, onChange: e => setPrivacyAccepted(e.target.checked) }), privacyParams?.texts?.newTxt1 ? (_jsx("span", { dangerouslySetInnerHTML: { __html: privacyParams.texts.newTxt1 } })) : (_jsx("span", { children: t('interview_privacy', 'Elfogadom az adatkezelési tájékoztatót, és hozzájárulok, hogy az önéletrajzomat feldolgozzátok.') }))] }), privacyParams?.read_acknowledgement_checkbox && (_jsxs("label", { className: "career-interview-privacy", children: [_jsx("input", { type: "checkbox", checked: readAckAccepted, onChange: e => setReadAckAccepted(e.target.checked) }), privacyParams?.texts?.privacy_text ? (_jsx("span", { dangerouslySetInnerHTML: { __html: privacyParams.texts.privacy_text } })) : (_jsx("span", { children: t('interview_read_ack', 'Elolvastam és megértettem az adatkezelési tájékoztatót.') }))] })), cvControlsVisible && (_jsx("input", { ref: fileInputRef, type: "file", accept: ".pdf,.doc,.docx,.rtf,.txt,.odt,.png,.jpg,.jpeg", style: { display: 'none' }, onChange: e => {
                        const file = e.target.files && e.target.files[0];
                        if (file) {
                            setCvFile(file);
                        }
                    } })), cvControlsVisible && (_jsx("input", { type: "url", className: "career-interview-input career-interview-linkedin", placeholder: t('interview_linkedin_placeholder', 'vagy illeszd be a LinkedIn profilod linkjét...'), value: linkedinUrl, disabled: busy || step === 'extracting', onChange: e => setLinkedinUrl(e.target.value) })), _jsxs("div", { className: "career-interview-actions", children: [cvControlsVisible && (_jsx("button", { type: "button", className: "career-interview-btn-secondary", onClick: () => fileInputRef.current && fileInputRef.current.click(), children: cvFile ? cvFile.name : t('interview_pick_cv', 'Önéletrajz kiválasztása') })), _jsx("button", { type: "button", className: "career-interview-btn-primary", disabled: !privacyAccepted
                                || (privacyParams?.read_acknowledgement_checkbox && !readAckAccepted)
                                || (cvControlsVisible && !cvFile && linkedinUrl.trim() === '') || busy || step === 'extracting', onClick: () => {
                                if (cvControlsVisible && cvFile) {
                                    handleExtract(cvFile);
                                }
                                else if (cvControlsVisible && linkedinUrl.trim() !== '') {
                                    handleLinkedin(linkedinUrl.trim());
                                }
                                else if (!cvControlsVisible) {
                                    startChat('');
                                }
                            }, children: step === 'extracting'
                                ? t('interview_extracting', `${agentName} olvassa az önéletrajzod...`)
                                : !cvControlsVisible || chatFill
                                    ? t('interview_start_chat', 'Kezdjük!')
                                    : t('interview_start', 'Folytatás') })] }), _jsx("button", { type: "button", className: "career-interview-link", onClick: fallbackToForm, children: t('interview_switch_form', 'Kitöltés űrlapon') })] }) }));
}
